/**
 * Does this browser ACTUALLY render CSS 3-D transforms, or does it flatten them?
 *
 * `@supports (transform-style: preserve-3d)` is not enough: several Android WebViews accept
 * the declaration and then composite the subtree flat, which is exactly the "the die is not
 * 3-D on Android" report — the cube span rotates, but its faces never separate in depth, so
 * it reads as a square being spun.
 *
 * So this MEASURES it instead of asking. Under a perspective ancestor, a child pushed toward
 * the camera with `translateZ` must be drawn LARGER. If the subtree is flattened, translateZ
 * has no effect and the measured width is unchanged.
 *
 * Cached: the answer cannot change for the life of the document, and the probe touches layout.
 */
let cached: boolean | null = null;
let platform: string | null = null;

/**
 * Telegram platforms whose WebView renders CSS 3-D badly REGARDLESS of what it reports.
 *
 * This is not a guess: on Telegram Android the probe below PASSES (the engine says depth
 * works) and the cube still composites flat — and janks while doing it, because a
 * `preserve-3d` subtree nested inside several `overflow:hidden` ancestors falls off the
 * fast path there. A capability probe cannot detect "supported but rendered wrong", so this
 * platform is opted out by name. iOS renders it correctly and keeps the cube.
 */
const FLATTENS_3D = new Set(["android", "android_x"]);

/** Called once at boot with `tgWebAppPlatform` — Telegram's own signal, not a UA sniff. */
export function setPlatformFor3D(value: string | null): void {
  platform = value;
  cached = null; // re-decide with the platform known
}

export function supports3DTransforms(): boolean {
  if (cached !== null) return cached;
  if (platform !== null && FLATTENS_3D.has(platform)) {
    cached = false;
    return cached;
  }
  if (typeof document === "undefined") return true; // SSR/tests — assume yes, nothing renders
  try {
    const host = document.createElement("div");
    host.setAttribute("aria-hidden", "true");
    host.style.cssText =
      "position:absolute;left:-9999px;top:0;width:100px;height:100px;perspective:100px;pointer-events:none";
    const stage = document.createElement("div");
    stage.style.cssText = "transform-style:preserve-3d;width:100px;height:100px";
    const probe = document.createElement("div");
    // +50px toward a 100px-away camera → a real 3-D pipeline draws this at 2x.
    probe.style.cssText = "width:100px;height:100px;transform:translateZ(50px)";
    const control = document.createElement("div");
    control.style.cssText = "width:100px;height:100px";
    stage.appendChild(probe);
    stage.appendChild(control);
    host.appendChild(stage);
    document.body.appendChild(host);
    const pushed = probe.getBoundingClientRect().width;
    const flat = control.getBoundingClientRect().width;
    host.remove();
    // NO LAYOUT ENGINE (happy-dom, jsdom) reports 0 for everything. That is "cannot tell",
    // NOT "flattened" — degrading there would swap the die out under every test and in any
    // non-rendering environment. Only a real measurement counts as evidence.
    if (flat <= 0) {
      cached = true;
      return cached;
    }
    // Anything meaningfully wider than its layout box means depth was actually applied.
    cached = pushed > flat * 1.1;
  } catch {
    cached = false; // if we cannot tell, take the path that always looks right
  }
  return cached;
}

/** TEST SEAM — force the answer (and reset with `null`). */
export function __set3DSupportForTests(value: boolean | null): void {
  cached = value;
  if (value === null) platform = null;
}
